import {
    IActionHandler,
    ITaskService,
    ITaskPersistenceManager,
    ITaskAssociationManager,
    TaskMetadata
} from '../types/internal-types';
import {
    AITask,
    ITaskRepository,
    TaskAPIError,
    AITaskRequestContext,
    TaskOptions,
    GetTaskOptions
} from '../types/public-types';
import {
    ILogger,
    IStreamingProviderFactory,
    IAppLifecycleProvider
} from '@honeybook/core';
import { AIRequest } from '../types/server-types';
import { HoneybookAITask } from './honeybook-ai-task';
import { IStorage } from '@honeybook/core';
import { TaskPersistenceManager } from './task-persistence-manager';
import { TaskAssociationManager } from './task-association-manager';

export interface TaskRepositoryOptions {
    taskClient: ITaskService;
    streamingProviderFactory: IStreamingProviderFactory;
    actionManager: IActionHandler;
    logger: ILogger;
    storage: IStorage;
    appLifecycleProvider: IAppLifecycleProvider;
}

export class TaskRepository implements ITaskRepository {
    // Map to store tasks by ID
    private tasks = new Map<string, AITask>();
    // Helper for managing taskKey associations
    private associations: ITaskAssociationManager;
    private readonly taskClient: ITaskService;
    private readonly streamingProviderFactory: IStreamingProviderFactory;
    private readonly actionManager: IActionHandler;
    private readonly logger: ILogger;
    private readonly persistenceManager: ITaskPersistenceManager;
    private initializationPromise: Promise<void> | null = null;

    constructor({
        taskClient,
        streamingProviderFactory,
        actionManager,
        logger,
        storage,
        appLifecycleProvider
    }: TaskRepositoryOptions) {
        this.taskClient = taskClient;
        this.streamingProviderFactory = streamingProviderFactory;
        this.actionManager = actionManager;
        this.logger = logger;
        this.persistenceManager = new TaskPersistenceManager(storage, logger);
        this.associations = new TaskAssociationManager();

        // Register for app lifecycle events
        appLifecycleProvider.onBeforeExit(() => this.persistTaskState());
    }

    async createTask(
        feature: string,
        requests: AIRequest[],
        options?: TaskOptions,
        context?: AITaskRequestContext
    ): Promise<AITask> {
        try {
            const taskResponse = await this.taskClient.executeTask({
                feature,
                requests,
                context
            });

            // Create and register new task
            const newTask = this.instantiateAndRegisterTask(
                taskResponse,
                options?.timeout
            );

            // Store taskKey association if provided
            if (options?.taskKey) {
                this.associations.setAssociation(
                    feature,
                    options.taskKey,
                    newTask.id
                );
            }

            return newTask;
        } catch (error) {
            // Let TaskAPIError propagate, wrap others
            if (error instanceof TaskAPIError) {
                throw error;
            }
            throw new TaskAPIError('Failed to create task', error);
        }
    }

    async getTask(
        feature: string,
        options: GetTaskOptions
    ): Promise<AITask | null> {
        // Ensure initialization has completed
        await this.initialize();

        // Determine if this is taskId or taskKey lookup
        if (options.taskId) {
            // Direct taskId lookup
            return this.getTaskById(options.taskId);
        } else {
            // TaskKey lookup - resolve to taskId first
            const taskId = this.associations.getTaskId(
                feature,
                options.taskKey
            );

            if (!taskId) {
                return null; // No association found
            }

            return this.getTaskById(taskId);
        }
    }

    /**
     * Initialize the task repository by restoring tasks from storage.
     * Only fetches tasks that are actually stored locally (optimization).
     */
    async initialize(): Promise<void> {
        this.initializationPromise ??= this.performInitialization();
        return this.initializationPromise;
    }

    /**
     * Helper method for actual task retrieval by taskId
     */
    private async getTaskById(taskId: string): Promise<AITask | null> {
        // Check if task is already in memory cache
        const cachedTask = this.tasks.get(taskId);
        if (cachedTask) {
            return cachedTask;
        }

        try {
            // Fetch task from backend
            const taskResponse = await this.taskClient.getTask(taskId);
            if (!taskResponse) {
                return null;
            }

            // Create and register task
            return this.instantiateAndRegisterTask(taskResponse);
        } catch (error) {
            if (error instanceof TaskAPIError) {
                throw error;
            }
            throw new TaskAPIError('Failed to fetch task', error);
        }
    }

    /**
     * Create a new task instance and register it with the system
     */
    private instantiateAndRegisterTask(
        taskResponse: any,
        timeout?: number,
        lastProcessedOrder?: number
    ): AITask {
        const task = new HoneybookAITask({
            taskResponse,
            taskClient: this.taskClient,
            streamingProviderFactory: this.streamingProviderFactory,
            timeout,
            logger: this.logger,
            lastProcessedOrder
        });

        // Add to cache and register with action manager
        this.tasks.set(task.id, task);
        this.actionManager.registerTask(task);

        return task;
    }

    private async performInitialization(): Promise<void> {
        try {
            // Get stored task metadata
            const storedTasks = await this.persistenceManager.getStoredTasks();

            if (storedTasks.length === 0) {
                // No stored tasks to restore
                return;
            }

            // Create metadata map for quick lookup
            const metadataMap = new Map<string, TaskMetadata>();
            for (const taskMetadata of storedTasks) {
                metadataMap.set(taskMetadata.id, taskMetadata);
            }

            // Clean up storage immediately after reading it to prevent stale retries
            // Do this before attempting restoration to avoid leaving stale state on failure
            await this.persistenceManager.cleanup();

            // Get only specific tasks from backend using task IDs
            const taskListResponse = await this.taskClient.fetchTasks(
                Array.from(metadataMap.keys())
            );

            // Restore all tasks returned from backend and build taskKey associations
            for (const taskResponse of taskListResponse.tasks) {
                try {
                    const taskMetadata = metadataMap.get(
                        taskResponse.ai_task_id
                    );
                    if (!taskMetadata) {
                        continue; // Skip if no metadata found
                    }

                    // Create and register restored task
                    this.instantiateAndRegisterTask(
                        taskResponse,
                        taskMetadata.timeout,
                        taskMetadata.lastProcessedOrder
                    );

                    // Restore taskKey association if present
                    if (taskMetadata.taskKey) {
                        this.associations.setAssociation(
                            taskMetadata.feature,
                            taskMetadata.taskKey,
                            taskResponse.ai_task_id
                        );
                        this.logger.debug(
                            `Restored taskKey association: ${taskMetadata.feature}_${taskMetadata.taskKey} -> ${taskResponse.ai_task_id}`
                        );
                    }

                    this.logger.debug(
                        `Restored task ${taskResponse.ai_task_id} with lastProcessedOrder: ${taskMetadata.lastProcessedOrder}`
                    );
                } catch (error) {
                    this.logger.warn(
                        `Failed to restore task ${taskResponse.ai_task_id} from storage:`,
                        error
                    );
                    // Continue with other tasks even if one fails
                }
            }
        } catch (error) {
            this.logger.error('Failed to initialize task repository:', error);
            // Don't throw - gracefully degrade to working without persistence
        }
    }

    /**
     * Persist current task state to storage on app exit
     */
    private async persistTaskState(): Promise<void> {
        const tasksMetadata = Array.from(this.tasks.entries())
            .filter(([, task]) => task.status === 'active')
            .map(([taskId, task]) => ({
                id: taskId,
                feature: task.feature,
                lastProcessedOrder: task.lastProcessedOrder,
                timeout: task.timeout,
                taskKey: this.associations.getTaskKey(taskId)
            }));

        await this.persistenceManager.persistTasks(tasksMetadata);
    }
}
